import client from './client';
import { toRawListParams } from './paging';
import { Company, PaginatedResponse, ListParams } from '../types/index';

// Bug #331 / WI #323: the backend serialises snake_case and nothing converts
// it — `api/client.ts`'s response interceptor is the identity function. This
// module previously typed the raw payload as the camelCase `Company`, which
// TypeScript accepted happily and which made every non-identical field
// resolve to `undefined` at runtime. Only `id`, `name` and `domain` survived,
// because they are spelled the same on both sides — so the companies list
// showed a blank Source system, a blank Created date, and an Inactive badge
// on every row regardless of the real value.
//
// Mapping once here follows the `api/users.ts` pattern (state 1 in the API
// case-convention note), the same way `api/projects.ts` was fixed under #328.
interface RawCompany {
  id: string;
  name: string;
  domain?: string | null;
  source_system?: string | null;
  source_id?: string | null;
  source_url?: string | null;
  is_active?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: string | null;
}

const mapCompany = (c: RawCompany): Company => ({
  id: c.id,
  name: c.name,
  domain: c.domain ?? undefined,
  sourceSystem: c.source_system ?? undefined,
  sourceId: c.source_id ?? undefined,
  sourceUrl: c.source_url ?? undefined,
  isActive: Boolean(c.is_active),
  createdAt: c.created_at ?? '',
  updatedAt: c.updated_at ?? '',
  createdBy: c.created_by ?? '',
});

interface RawPaginatedCompanies {
  items: RawCompany[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Requests go the other way: the backend binds snake_case bodies, so anything
// we send has to be snake_case too.
//
// `source_system` / `source_id` are only sent when the caller actually has
// them. Bug #331 made both optional on `CompanyCreate`; the backend records a
// company created without them as `source_system='manual'` with a generated
// identifier, so the Add company modal does not have to invent a CRM identity
// for a company someone is typing in by hand.
const toRawCompany = (data: Partial<Company>): Record<string, unknown> => {
  const raw: Record<string, unknown> = {};
  if (data.name !== undefined) raw.name = data.name;
  if (data.domain !== undefined) raw.domain = data.domain;
  if (data.sourceSystem !== undefined) raw.source_system = data.sourceSystem;
  if (data.sourceId !== undefined) raw.source_id = data.sourceId;
  if (data.sourceUrl !== undefined) raw.source_url = data.sourceUrl;
  return raw;
};

// Query params bind snake_case too. WI #337 moved the mapper to `api/paging.ts`
// so every list module shares one copy instead of each growing its own.
export const getCompanies = async (params?: ListParams): Promise<PaginatedResponse<Company>> => {
  const response = await client.get<RawPaginatedCompanies>('/companies', {
    params: toRawListParams(params),
  });
  const data = response.data;
  return {
    items: (data.items || []).map(mapCompany),
    total: data.total,
    page: data.page,
    pageSize: data.page_size,
    pages: data.total_pages,
  };
};

export const getCompany = async (id: string): Promise<Company> => {
  const response = await client.get<RawCompany>(`/companies/${id}`);
  return mapCompany(response.data);
};

export const createCompany = async (data: Partial<Company>): Promise<Company> => {
  const response = await client.post<RawCompany>('/companies', toRawCompany(data));
  return mapCompany(response.data);
};

export const updateCompany = async (id: string, data: Partial<Company>): Promise<Company> => {
  // PATCH, not PUT — the backend only exposes PATCH /companies/{id}, so the
  // previous PUT would have 405'd had anything ever called it.
  const response = await client.patch<RawCompany>(`/companies/${id}`, toRawCompany(data));
  return mapCompany(response.data);
};

// Deactivation is a soft-delete on the backend: DELETE flips is_active=false
// and records an audit entry. The previous PATCH of `{isActive: false}` could
// never have worked — the field is camelCase and `CompanyUpdate` does not
// accept `is_active` at all, so the request was silently a no-op that
// returned 200. Same shape and same reasoning as `deactivateProject` (#330)
// and `deactivateUser` (#143).
export const deactivateCompany = async (id: string): Promise<void> => {
  await client.delete(`/companies/${id}`);
};
